import type { ActivationGroup } from '@/features/activationGroup/types'
import type { BidType } from '@/features/bidding/constants'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { PriceLadderType } from '@/features/bidding/utils/getBidConfig'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBid } from '@/features/bidding/utils/model/buildAggregatedBid'
import {
  buildAggregatedBid,
  fixPricesToZeroWhenOfferedVolumeWasZero,
} from '@/features/bidding/utils/model/buildAggregatedBid'
import { validateAggregatedBid } from '@/features/bidding/utils/validation/aggregatedBidValidations'

export type AggregatedBidWrapper = {
  activationGroup: ActivationGroup
  aggregatedBid: AggregatedBid
  state: 'valid' | 'invalid' | 'pending'
}

/**
 * Maps activation group bids to aggregated bids with validation state.
 *
 * For each activation group, this function:
 * 1. Filters bids belonging to that group
 * 2. Builds an aggregated bid from the filtered bids
 * 3. Fixes prices to zero when offered volume was zero
 * 4. Validates the aggregated bid against the activation group
 * 5. Returns a wrapper object with the activation group, aggregated bid, and validation state
 *
 * @param activationGroupBids - Array of bids with activation group UUIDs
 * @param bidActivationGroups - Array of eligible activation groups
 * @param priceLadderType - Type of price ladder (CUMULATIVE or INCREMENTAL)
 * @param groupParams - The grouped bid parameters (deliveryDay, marketProgram, portfolioCode, bidType)
 * @returns Map of activation group UUID to aggregated bid wrapper
 * @throws Error if more than one bid exists for a single activation group
 */
export function mapActivationGroupBidsToAggregatedBids(
  activationGroupBids: ExtendedBid[],
  bidActivationGroups: ActivationGroup[] | null,
  priceLadderType: PriceLadderType,
  groupParams: GroupedBidParams,
): Map<string, AggregatedBidWrapper> {
  const bidsPerActivationGroup = new Map<string, AggregatedBidWrapper>()

  if (!bidActivationGroups) {
    return bidsPerActivationGroup
  }

  for (const group of bidActivationGroups) {
    const bids = activationGroupBids.filter((bid) => bid.activationGroupUuid === group.uuid)
    if (bids.length === 1) {
      const aggregatedBid = buildAggregatedBid(bids, priceLadderType)

      const fixedAggregatedBid = fixPricesToZeroWhenOfferedVolumeWasZero(aggregatedBid)

      bidsPerActivationGroup.set(group.uuid, {
        activationGroup: group,
        state: 'pending',
        // we build an 'aggregated bid' of just one bid so that we can reuse the same data grid below
        // for both strategies: aggregated view and per group view
        // so in this case we aren't aggregating anything really, just converting from ExtendedBid to AggregatedBid
        aggregatedBid: fixedAggregatedBid,
      })
    } else if (bids.length > 1) {
      throw new Error(`There is more than one bid for activation group ${group.uuid} which should never happen`)
    }
  }

  return withRevalidatedStates(bidsPerActivationGroup, groupParams.bidType)
}

/** Cross-activation-group rules make each group's state depend on the others, so all are revalidated together. */
export function withRevalidatedStates(
  bidsPerActivationGroup: Map<string, AggregatedBidWrapper>,
  bidType: BidType,
): Map<string, AggregatedBidWrapper> {
  return new Map(
    [...bidsPerActivationGroup].map(([uuid, wrapper]) => [
      uuid,
      {
        ...wrapper,
        state: validateAggregatedBid(wrapper.aggregatedBid, wrapper.activationGroup, bidsPerActivationGroup, bidType),
      },
    ]),
  )
}
